import type { StoreInventoryResponse, StoreItem } from './store-inventory.types';

export type { StoreItem, StoreInventoryResponse };

/**
 * Client-safe: M2M and THEGAME_API_BASE_URL only exist on the server.
 * This calls the Ecosystem BFF at `/api/store/inventory` (no secrets in the browser).
 */
export async function getStoreInventory(
  includeLegacy: boolean = false,
  page: number = 1,
  limit: number = 20,
  category?: string,
  collection?: string,
  search?: string,
): Promise<StoreInventoryResponse> {
  try {
    const q = new URLSearchParams();
    if (includeLegacy) {
      q.set('legacy', 'true');
    }
    if (page) q.set('page', page.toString());
    if (limit) q.set('limit', limit.toString());
    if (category) q.set('category', category);
    if (collection) q.set('collection', collection);
    if (search) q.set('search', search);

    const res = await fetch(`/api/store/inventory?${q.toString()}`, {
      method: 'GET',
      cache: 'no-store',
    });
    let data: StoreInventoryResponse;
    try {
      data = (await res.json()) as StoreInventoryResponse;
    } catch {
      return { success: false, error: `Invalid response from store API (${res.status})` };
    }
    if (!res.ok) {
      return {
        success: false,
        error: data.error || `Store API error (${res.status})`,
      };
    }
    return data;
  } catch (error) {
    console.error('[TheGame Store Client] Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch store inventory',
    };
  }
}

export async function getFeaturedStoreItems(): Promise<StoreInventoryResponse> {
  try {
    const res = await fetch('/api/store/featured', {
      method: 'GET',
      cache: 'no-store',
    });

    let data: StoreInventoryResponse;
    try {
      data = (await res.json()) as StoreInventoryResponse;
    } catch {
      return { success: false, error: `Invalid response from store featured API (${res.status})` };
    }

    if (!res.ok) {
      return {
        success: false,
        error: data.error || `Store featured API error (${res.status})`,
      };
    }

    return data;
  } catch (error) {
    console.error('[TheGame Store Client] Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch featured store items',
    };
  }
}

export async function getStoreCategories() {
  try {
    const res = await fetch('/api/store/categories');
    return await res.json();
  } catch (error) {
    console.error('Error fetching categories:', error);
    return { success: false, categories: [] };
  }
}
